import React, { useEffect, useState } from 'react';

import NativePdfSettingsDialog from '~/nativeLenses/NativePdfSettingsDialog';
import { useNativePrintMode } from '~/nativeLenses/nativePrintSession';
import { useNativeReportPdf } from '~/nativeLenses/nativeReportPdf';

import { Box, Menu, MenuItem } from '@mui/material';

import type { VendorData } from './dashboard';
import { assertPartnerSafe } from './export';

export type ReportPdfKind = 'partner' | 'internal';

export const INTERNAL_PDF_NOTICE = 'Internal report. Not for partners.';
export const INTERNAL_PDF_TITLE = 'Flash Post Campaign (Internal)';

/** Which PDF is printing, cleared once the print engine is done. */
export function useReportPdfKind() {
  const { printing } = useNativeReportPdf();
  const [kind, setKind] = useState<ReportPdfKind | null>(null);
  useEffect(() => {
    if (!printing) {
      setKind(null);
    }
  }, [printing]);
  return [kind, setKind] as const;
}

/** Paper-only warning on the internal PDF; never shown on screen. */
export function InternalPdfNotice({ kind }: { kind: ReportPdfKind | null }) {
  const printing = useNativePrintMode();
  if (!printing || kind !== 'internal') {
    return null;
  }
  return (
    <Box
      data-internal-pdf-notice=""
      sx={{ border: 2, borderColor: 'error.main', color: 'error.main', fontWeight: 700, px: 1.5, py: 1 }}
    >
      {INTERNAL_PDF_NOTICE}
    </Box>
  );
}

export default function ExportReportMenu({
  anchor,
  view,
  vendor,
  assertCurrent,
  onClose,
  onCsv,
  onHtml,
  onPdfKind,
  onError,
}: {
  anchor: HTMLElement | null;
  view: string | null;
  vendor: VendorData | undefined;
  assertCurrent: () => void;
  onClose: () => void;
  onCsv: () => void;
  onHtml: () => void;
  onPdfKind: (kind: ReportPdfKind) => void;
  onError: (message: string) => void;
}) {
  const pdf = useNativeReportPdf();
  const [dialog, setDialog] = useState<ReportPdfKind | null>(null);
  function check(kind: ReportPdfKind) {
    assertCurrent();
    if (kind === 'internal') {
      return;
    }
    if (view !== 'Vendor') {
      throw Error('Partner reports are only available from the Vendor tab.');
    }
    if (!vendor) {
      throw Error('Wait for complete data for this selection before exporting.');
    }
    assertPartnerSafe(vendor);
  }
  function attempt(action: () => void) {
    try {
      action();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'The report could not be exported.');
    }
  }
  function choose(kind: ReportPdfKind) {
    onClose();
    attempt(() => {
      check(kind);
      onError('');
      setDialog(kind);
    });
  }
  return (
    <>
      <Menu anchorEl={anchor} open={!!anchor} onClose={onClose}>
        <MenuItem onClick={onCsv}>CSV · internal report data</MenuItem>
        {view === 'Vendor' && <MenuItem onClick={onHtml}>Partner report · HTML</MenuItem>}
        {view === 'Vendor' && <MenuItem onClick={() => choose('partner')}>Partner report · PDF</MenuItem>}
        <MenuItem onClick={() => choose('internal')}>Internal report · PDF</MenuItem>
      </Menu>
      <NativePdfSettingsDialog
        open={!!dialog}
        ready={pdf.ready}
        title={dialog === 'partner' ? 'Partner report PDF' : 'Internal report PDF'}
        onClose={() => setDialog(null)}
        onDownload={(settings) => {
          const kind = dialog;
          if (!kind) {
            return;
          }
          attempt(() => {
            check(kind);
            onPdfKind(kind);
            pdf.downloadPdf(settings, {
              title: kind === 'internal' ? INTERNAL_PDF_TITLE : `${vendor.property} · Flash Campaign Report`,
            });
          });
        }}
      />
    </>
  );
}
